import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, X, AlertCircle, Clock, CheckCircle, TrendingUp, TrendingDown, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useFuturesTrading, PositionHistoryEntry } from '../hooks/useFuturesTrading';
import { DatabaseFuturesPosition, MarketData } from '../hooks/useDatabase';
import { PropPositionHistoryEntry } from '../hooks/usePropFirmTrading';
import { CFD_INSTRUMENTS } from '../constants/tradingPairs';
import { supabase } from '../lib/supabaseClient';
import { TradingMode } from '../App';

interface FuturesMyOrdersProps {
  currentBtcPrice: number;
  futuresPositions: any[];
  marketData: MarketData[];
  onClosePosition: (positionId: string) => void;
  updateBalances?: (updates: { usdt_balance?: number }) => Promise<void>;
  openOrders?: any[];
  onCancelOrder?: (orderId: string) => Promise<boolean>;
  onCancelAllOrders?: () => Promise<boolean>;
  balances?: { usdt_balance?: number };
  propHistory?: PropPositionHistoryEntry[];
  challengeId?: string;
  selectedPair: string;
  tradingMode: TradingMode;
  onClosePropPosition?: (positionId: string) => Promise<boolean>;
  onCancelPropOrder?: (orderId: string) => Promise<boolean>;
  onCancelAllPropOrders?: () => Promise<boolean>;
  currentSelectedPairPrice: number;
}

interface FuturesOrder {
  id: string;
  user_id: string;
  symbol: string;
  type: 'limit' | 'market' | 'stop';
  side: 'buy' | 'sell';
  price: number | null;
  amount: number;
  leverage: number;
  status: 'open' | 'filled' | 'cancelled';
  created_at: string;
  filled_at: string | null;
  position_id: string | null;
}


const FuturesMyOrders: React.FC<FuturesMyOrdersProps> = ({
  currentBtcPrice,
  futuresPositions,
  marketData,
  onClosePosition,
  updateBalances,
  openOrders = [],
  onCancelOrder,
  onCancelAllOrders,
  balances,
  propHistory = [],
  challengeId,
  selectedPair,
  tradingMode,
  onClosePropPosition,
  onCancelPropOrder,
  onCancelAllPropOrders,
  currentSelectedPairPrice
}) => {
  const { t } = useTranslation();
  const { loadPositionHistory, positionHistory, loading, error } = useFuturesTrading();
  const [activeTab, setActiveTab] = useState('positions');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const tabs = [
    { key: 'positions', label: t('futures.positions') },
    { key: 'openOrders', label: t('futures.openOrders') },
    { key: 'positionHistory', label: t('futures.positionHistory') }
  ];
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [processingPositions, setProcessingPositions] = useState<Record<string, boolean>>({});
  
  // Pagination state for Position History  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Helper function to determine price precision based on symbol
  const getPricePrecision = useCallback((symbol: string): number => {
    // For CFD trading mode, use higher precision for forex pairs
    if (tradingMode === 'cfd') {
      // Forex pairs (6 characters, no USDT suffix)
      if (symbol.length === 6 && !symbol.includes('USDT')) {
        return 5; // 5 decimal places for forex
      }
      // Commodities (XAU, XAG, etc.)
      if (symbol.startsWith('XAU') || symbol.startsWith('XAG') || symbol.startsWith('XPT') || 
          symbol.startsWith('XPD') || symbol === 'WTIUSD' || symbol === 'NGUSD') {
        return 3; // 3 decimal places for precious metals
      }
      // Oil and other commodities
      if (symbol.includes('COPPER') || symbol.includes('ALU') || symbol.includes('ZINC')) {
        return 3; // 3 decimal places for industrial metals
      }
      // Stocks
      const stockSymbols = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NFLX', 'NVDA', 'BA', 'JPM'];
      if (stockSymbols.includes(symbol)) {
        return 2; // 2 decimal places for stocks
      }
    }
    
    // Default for crypto and other instruments
    return 2; // 2 decimal places for crypto and other instruments
  }, [tradingMode]);

  // Helper function to determine instrument type
  const getInstrumentType = useCallback((symbol: string): string => {
    if (tradingMode === 'cfd') {
      // For CFD mode, look up the instrument in CFD_INSTRUMENTS
      const instrument = CFD_INSTRUMENTS.find(item => item.symbol === symbol);
      if (instrument) {
        return instrument.type;
      }
    }
    
    // For non-CFD modes, check if it's crypto
    if (symbol.endsWith('USDT') || symbol.endsWith('BTC') || symbol.endsWith('ETH')) {
      return 'crypto';
    }
    
    // Default fallback
    return 'other';
  }, []);


  // Filter positions based on selectedPair and tradingMode
  const filteredFuturesPositions = useMemo(() => {
    if (challengeId) {
      return futuresPositions; // Show all positions for prop firm challenges
    } else if (tradingMode === 'cfd') {
      // In CFD mode, show all non-crypto positions
      return futuresPositions.filter(position => getInstrumentType(position.symbol) !== 'crypto');
    } else { // tradingMode === 'futures'
      // In Futures mode, show all crypto positions (not just the selected pair)
      return futuresPositions.filter(position => getInstrumentType(position.symbol) === 'crypto');
    }
  }, [futuresPositions, challengeId, tradingMode, selectedPair, getInstrumentType]);

  // Filter open orders based on selectedPair and tradingMode
  const filteredOpenOrders = useMemo(() => {
    if (challengeId) {
      return openOrders; // Show all orders for prop firm challenges
    } else if (tradingMode === 'cfd') {
      // In CFD mode, show all non-crypto orders
      return openOrders.filter(order => getInstrumentType(order.symbol) !== 'crypto');
    } else { // tradingMode === 'futures'
      // In Futures mode, filter by selectedPair and ensure it's a crypto pair
      return openOrders.filter(order => order.symbol === selectedPair && getInstrumentType(order.symbol) === 'crypto');
    }
  }, [openOrders, challengeId, tradingMode, selectedPair, getInstrumentType]);

  // Debug logs - only log when data changes
  useEffect(() => {
    console.log('FuturesMyOrders - Data update:', {
      challengeId,
      selectedPair,
      totalFuturesPositions: futuresPositions.length,
      filteredFuturesPositions: filteredFuturesPositions.length,
      totalOpenOrders: openOrders.length,
      filteredOpenOrders: filteredOpenOrders.length,
      futuresPositions: futuresPositions.map(p => ({ id: p.id, symbol: p.symbol })),
      openOrders: openOrders.map(o => ({ id: o.id, symbol: o.symbol }))
    });
  }, [challengeId, selectedPair, futuresPositions.length, filteredFuturesPositions.length, openOrders.length, filteredOpenOrders.length]);

  // Calculate real-time PnL and ROI for each position
  const positionsWithLiveData = filteredFuturesPositions.map(position => {
    let livePrice = 0;
    const symbol = position.symbol;
    
    // Use Supabase market data as the only source for all symbols
    if (marketData.find(data => data.symbol === symbol)?.price) {
      livePrice = marketData.find(data => data.symbol === symbol)!.price;
    }
    // Use entry price as fallback
    else if (position.entryPrice || position.entry_price) {
      livePrice = position.entryPrice || position.entry_price || 0;
    }
    
    // Get position properties with fallbacks for both naming conventions
    const entryPrice = position.entryPrice || position.entry_price || 0;
    const amount = position.amount || 0;
    const leverage = position.leverage || 1;
    const margin = position.margin || 0;
    const side = position.side || 'long';
    const liquidationPrice = position.liquidationPrice || position.liquidation_price || 0;
    
    // Calculate unrealized PnL based on live price (WITHOUT leverage multiplier)
    let unrealizedPnl = 0;
    if (side === 'long') {
      unrealizedPnl = (livePrice - entryPrice) * amount;
    } else {
      unrealizedPnl = (entryPrice - livePrice) * amount;
    }
    
    // Calculate ROI
    const roi = margin > 0 ? (unrealizedPnl / margin) * 100 : 0;
    
    return {
      ...position,
      currentPrice: livePrice,
      entryPrice,
      amount,
      leverage,
      margin,
      side,
      liquidationPrice,
      unrealizedPnl,
      roi
    };
  });

  const totalUnrealizedPnl = positionsWithLiveData.reduce((sum, pos) => sum + (pos.unrealizedPnl || 0), 0);
  const totalMargin = positionsWithLiveData.reduce((sum, pos) => sum + (pos.margin || 0), 0);
  const runningPositions = positionsWithLiveData.length;

  // Get the appropriate position history data
  const positionHistoryData = challengeId ? propHistory : positionHistory;
  
  // Calculate pagination for position history
  const totalPages = Math.ceil(positionHistoryData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentHistoryItems = positionHistoryData.slice(indexOfFirstItem, indexOfLastItem);
  
  // Reset to page 1 when switching tabs or when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, positionHistoryData.length]);
  
  // Pagination handlers
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };
  
  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Load position history when tab changes to Position History
  useEffect(() => {
    if (activeTab === 'positionHistory') {
      // Only load position history if we're not in a challenge view
      if (!challengeId) {
        loadPositionHistory();
      }
    }
  }, [activeTab, loadPositionHistory, challengeId]);

  // Update error message when error changes
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      
      // Clear error after 5 seconds
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleClosePosition = async (positionId: string) => {
    try {
      setProcessingPositions(prev => ({ ...prev, [positionId]: true }));
      
      let success = false;
      if (challengeId && onClosePropPosition) {
        // Use prop firm specific close function
        success = await onClosePropPosition(positionId);
      } else {
        // Use regular futures close function
        success = await onClosePosition(positionId);
      }
      
      if (success) {
        setSuccessMessage('Position closed successfully');
        
        // If updateBalances is available, refresh the balances to show returned margin
        if (updateBalances && !challengeId) {
          // For regular futures positions, we need to refresh balances from the parent component
          // The parent component (App.tsx) will handle this via fetchBalances in handleClosePosition
        }
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to close position');
    } finally {
      setProcessingPositions(prev => ({ ...prev, [positionId]: false }));
    }
  };

  const closeAllPositions = async () => {
    setIsProcessingAll(true);
    try {
      for (const position of filteredFuturesPositions) {
        await handleClosePosition(position.id);
      }
    } catch (error) {
      console.error('Error closing all positions:', error);
      setErrorMessage('Failed to close all positions');
    } finally {
      setIsProcessingAll(false);
    }
  };

  // Handle cancel order
  const handleCancelOrder = async (orderId: string) => {
    if (!onCancelOrder && !onCancelPropOrder) return;
    
    try {
      let success = false;
      if (challengeId && onCancelPropOrder) {
        // Use prop firm specific cancel function
        success = await onCancelPropOrder(orderId);
      } else if (onCancelOrder) {
        // Use regular futures cancel function
        success = await onCancelOrder(orderId);
      }
      
      if (success) {
        setSuccessMessage('Order cancelled successfully');
      } else {
        throw new Error('Failed to cancel order');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to cancel order');
    }
  };

  // Handle cancel all orders
  const handleCancelAllOrders = async () => {
    if (!onCancelAllOrders && !onCancelAllPropOrders) return;
    
    setIsProcessingAll(true);
    try {
      let success = false;
      if (challengeId && onCancelAllPropOrders) {
        // Use prop firm specific cancel all function
        success = await onCancelAllPropOrders();
      } else if (onCancelAllOrders) {
        // Use regular futures cancel all function
        success = await onCancelAllOrders();
      }
      
      if (success) {
        setSuccessMessage('All orders cancelled successfully');
      } else {
        throw new Error('Failed to cancel orders');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to cancel orders');
    } finally {
      setIsProcessingAll(false);
    }
  };

  // Format duration from seconds to human-readable format
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
  };

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-3 md:p-8 border border-slate-700/50 shadow-2xl h-full flex flex-col" translate="no">
      {/* Status Messages */}
      {errorMessage && (
        <div className="mb-4 md:mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-3 md:p-4 flex items-center gap-2 md:gap-3">
          <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
          <span className="text-red-400">{errorMessage}</span>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 md:mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-3 md:p-4 flex items-center gap-2 md:gap-3">
          <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
          <span className="text-green-400">{successMessage}</span>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-6 gap-3">
        <div className="w-full">
          <div className="flex gap-2 md:gap-4 overflow-x-auto hide-scrollbar pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`text-xs md:text-sm font-medium px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-all duration-300 whitespace-nowrap ${
                  activeTab === tab.key 
                    ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Assets Section */}
        <div className="bg-slate-900/30 rounded-xl p-3 md:p-6 w-full md:min-w-96 border border-slate-600/30 shadow-lg">
          <h4 className="text-cyan-400 font-semibold mb-2 md:mb-4 text-sm md:text-base">{t('futures.assets')}</h4>
          <div className="grid grid-cols-3 md:grid-cols-2 gap-2 md:gap-4 text-xs md:text-base">
            <div className="col-span-3 md:col-span-2">
              <span className="text-slate-400 text-xs">{t('futures.overallBalance')}:</span>
              <div className="text-white font-mono text-base md:text-xl">${totalMargin.toFixed(2)} USD</div>
            </div>
            <div>
              <span className="text-slate-400 text-xs">{t('futures.available')}:</span>
              <div className="text-white font-mono text-xs md:text-base">{balances?.usdt_balance?.toFixed(2) || '0'} USDT</div>
            </div>
            <div>
              <span className="text-slate-400 text-xs">{t('futures.unrealized')}:</span>
              <div className={`font-mono text-xs md:text-base ${totalUnrealizedPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {totalUnrealizedPnl.toFixed(2)}
              </div>
            </div>
            <div>
              <span className="text-slate-400 text-xs">{t('futures.positions')}:</span>
              <div className="text-white font-mono text-xs md:text-base">{runningPositions}</div>
            </div>
            <div className="hidden md:block">
              <span className="text-slate-400 text-xs">{t('futures.burning')}:</span>
              <div className="text-white font-mono text-xs md:text-base">0</div>
            </div>
            <div>
              <span className="text-slate-400 text-xs">{t('futures.orders')}:</span>
              <div className="text-white font-mono text-xs md:text-base">{openOrders.length}</div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-2 mt-3 md:mt-4">
            <button 
              onClick={closeAllPositions}
              disabled={isProcessingAll || filteredFuturesPositions.length === 0 || loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-2 md:py-3 rounded-lg md:rounded-xl text-sm md:text-base font-medium md:font-semibold transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-1 md:gap-2"
            >
              {isProcessingAll ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t('futures.processing')}
                </>
              ) : (
                t('futures.closeAllPositions')
              )}
            </button>
            <button 
              onClick={handleCancelAllOrders}
              disabled={isProcessingAll || filteredOpenOrders.length === 0 || loading || (!onCancelAllOrders && !onCancelAllPropOrders)}
              className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-2 md:py-3 rounded-lg md:rounded-xl text-sm md:text-base font-medium md:font-semibold transition-all duration-300 shadow-lg shadow-red-500/25 flex items-center justify-center gap-1 md:gap-2"
            >
              {isProcessingAll ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t('futures.processing')}
                </>
              ) : (
                t('futures.cancelAllOrders')
              )}
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'positions' && (
        <div className="flex-1 flex flex-col" translate="no">
          <div className="hidden lg:flex justify-between text-xs text-slate-400 mb-4 font-medium">
            <span className="text-xs md:text-sm">{t('futures.symbol')}</span>
            <span className="text-xs md:text-sm">{t('futures.size')}</span>
            <span className="text-xs md:text-sm">{t('futures.side')}</span>
            <span className="text-xs md:text-sm">{t('futures.entryPrice')}</span>
            <span className="text-xs md:text-sm">{t('futures.currentPrice')}</span>
            <span className="text-xs md:text-sm">{t('futures.liquidationPrice')}</span>
            <span className="text-xs md:text-sm">{t('futures.margin')}</span>
            <span className="text-xs md:text-sm">{t('futures.pnl')} ({t('futures.roi')})</span>
            <span className="text-xs md:text-sm"></span>
          </div>

          {positionsWithLiveData.length > 0 ? (
            <div className="space-y-3 flex-1 overflow-auto" translate="no">
              {positionsWithLiveData.map((position, index) => (
                <div key={position.id} className="flex flex-wrap md:flex-nowrap items-center justify-between bg-slate-900/30 px-3 md:px-6 py-3 md:py-4 rounded-xl hover:bg-slate-900/50 transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50 shadow-lg">
                  <div className="w-1/2 md:w-auto mb-2 md:mb-0">
                    <span className="text-white font-medium text-sm md:text-base">{position.symbol}</span>
                  </div>
                  <div className="w-1/2 md:w-auto mb-2 md:mb-0 text-right md:text-left">
                    <span className={`font-mono text-sm md:text-base ${position.side === 'long' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(position.amount || 0).toFixed(4)}
                    </span>
                  </div>
                  <div className="w-1/2 md:w-auto mb-2 md:mb-0">
                    <span className={`text-xs md:text-sm font-bold px-2 md:px-3 py-1 rounded-lg ${
                      position.side === 'long' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                    }`}>
                      {(position.side || 'long').toUpperCase()}
                    </span>
                  </div>
                  <div className="w-1/2 md:w-auto mb-2 md:mb-0 text-right md:text-left">
                    <div className="text-xs text-slate-400 md:hidden">{t('futures.entryPrice')}</div>
                    <span className="text-slate-300 font-mono text-sm md:text-base">{(position.entryPrice || 0).toFixed(getPricePrecision(position.symbol))}</span>
                  </div>
                  <div className="w-1/2 md:w-auto mb-2 md:mb-0">
                    <div className="text-xs text-slate-400 md:hidden">{t('futures.currentPrice')}</div>
                    <span className="text-slate-300 font-mono text-sm md:text-base">
                      {(position.currentPrice || 0).toFixed(getPricePrecision(position.symbol))}
                    </span>
                  </div>
                  <div className="hidden lg:block text-red-400 font-mono text-sm md:text-base">{(position.liquidationPrice || 0).toFixed(getPricePrecision(position.symbol))}</div>
                  <div className="hidden lg:block text-slate-300 font-mono text-sm md:text-base">{(position.margin || 0).toFixed(2)}</div>
                  <div className="hidden lg:block text-right">
                    <div className={`font-mono text-sm md:text-base ${(position.unrealizedPnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(position.unrealizedPnl || 0).toFixed(2)}
                    </div>
                    <div className={`text-xs md:text-sm ${(position.roi || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      ({(position.roi || 0).toFixed(2)}%)
                    </div>
                  </div>
                  {/* Mobile-only PnL display */}
                  <div className="w-1/2 md:w-auto mb-2 md:mb-0 lg:hidden">
                    <div className="text-xs text-slate-400">{t('futures.pnl')}</div>
                    <div className={`font-mono text-sm md:text-base ${(position.unrealizedPnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(position.unrealizedPnl || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="w-1/2 md:w-auto mb-2 md:mb-0 text-right">
                    <button 
                      onClick={() => handleClosePosition(position.id)}
                      disabled={loading || processingPositions[position.id]}
                      className="text-slate-400 hover:text-red-400 transition-colors p-2 hover:bg-red-400/10 rounded-lg disabled:opacity-50"
                    >
                      {processingPositions[position.id] ? (
                        <div className="w-4 h-4 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                      ) : (
                        <X size={16} />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 flex-1">
              <Package size={64} className="mb-6 opacity-30" />
              <p className="text-lg">{t('futures.noPositionsFound')}</p>
              <p className="text-sm text-slate-600 mt-2">{t('futures.positionsWillAppear')}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'openOrders' && (
        <div className="flex-1 flex flex-col" translate="no">
          <div className="hidden lg:flex justify-between text-xs text-slate-400 mb-4 font-medium">
            <span className="text-xs md:text-sm">{t('common.date')}</span>
            <span className="text-xs md:text-sm">{t('futures.symbol')}</span>
            <span className="text-xs md:text-sm">{t('common.type')}</span>
            <span className="text-xs md:text-sm">{t('futures.side')}</span>
            <span className="text-xs md:text-sm">{t('futures.price')}</span>
            <span className="text-xs md:text-sm">{t('futures.amount')}</span>
            <span className="text-xs md:text-sm">{t('futures.leverage')}</span>
            <span className="text-xs md:text-sm">{t('common.status')}</span>
            <span className="text-xs md:text-sm"></span>
          </div>
          
          {loadingOrders ? (
            <div className="flex justify-center py-8 flex-1">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredOpenOrders.length > 0 ? (
            <div className="space-y-3 flex-1 overflow-auto" translate="no">
              {filteredOpenOrders.map((order) => (
                <div key={order.id} className="flex flex-wrap items-center justify-between bg-slate-900/30 px-3 md:px-6 py-3 md:py-4 rounded-xl hover:bg-slate-900/50 transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50 shadow-lg">
                  <div className="w-full md:w-auto mb-2 md:mb-0 flex justify-between items-center">
                    <span className="text-slate-300 text-xs md:text-sm font-mono">
                      {new Date(order.createdAt || order.created_at).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <span className="text-blue-400 text-xs md:hidden">Open</span>
                  </div>
                  
                  <div className="w-1/2 md:w-auto mb-2 md:mb-0">
                    <span className="text-white font-medium text-sm md:text-base">{order.symbol}</span>
                  </div>
                  
                  <div className="w-1/2 md:w-auto mb-2 md:mb-0 text-right md:text-left">
                    <span className={`text-xs md:text-sm font-bold px-2 py-1 rounded-lg ${
                      order.side === 'buy' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                    }`}>
                      {order.side.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="w-1/3 md:w-auto mb-2 md:mb-0">
                    <div className="text-xs text-slate-400 md:hidden">{t('common.type')}</div>
                    <span className="text-slate-300 text-xs md:text-sm capitalize">{order.type}</span>
                  </div>
                  
                  <div className="w-1/3 md:w-auto mb-2 md:mb-0">
                    <div className="text-xs text-slate-400 md:hidden">{t('futures.price')}</div>
                    <span className="text-slate-300 font-mono text-xs md:text-sm">{order.price?.toFixed(getPricePrecision(order.symbol)) || 'Market'}</span>
                  </div>
                  
                  <div className="w-1/3 md:w-auto mb-2 md:mb-0">
                    <div className="text-xs text-slate-400 md:hidden">{t('futures.amount')}</div>
                    <span className="text-slate-300 font-mono text-xs md:text-sm">{(order.amount || 0).toFixed(4)}</span>
                  </div>
                  
                  <div className="hidden md:block lg:block text-orange-400 font-mono text-xs md:text-base">{order.leverage || 1}x</div>
                  <div className="hidden md:block text-blue-400 text-sm md:text-base">{t('common.status')}</div>
                  
                  <div className="w-full md:w-auto flex justify-end mt-2 md:mt-0">
                    <button 
                      onClick={() => (onCancelOrder || onCancelPropOrder) && handleCancelOrder(order.id)}
                      disabled={loading || (!onCancelOrder && !onCancelPropOrder)}
                      className="text-slate-400 hover:text-red-400 transition-colors p-2 hover:bg-red-400/10 rounded-lg disabled:opacity-50"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                      ) : (
                        <X size={16} />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 flex-1">
              <Package size={64} className="mb-6 opacity-30" />
              <p className="text-lg">{t('futures.noActiveOrders')}</p>
              <p className="text-sm text-slate-600 mt-2">{t('futures.ordersWillAppear')}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'positionHistory' && (
        <div className="flex-1 flex flex-col" translate="no">
          <div className="hidden lg:flex justify-between text-xs text-slate-400 mb-4 font-medium">
            <span className="text-xs md:text-sm">{t('common.date')}</span>
            <span className="text-xs md:text-sm">{t('futures.symbol')}</span>
            <span className="text-xs md:text-sm">{t('futures.side')}</span>
            <span className="text-xs md:text-sm">{t('futures.entryPrice')}</span>
            <span className="text-xs md:text-sm">Exit {t('futures.price')}</span>
            <span className="text-xs md:text-sm">{t('futures.pnl')}</span>
            <span className="text-xs md:text-sm">{t('futures.roi')}</span>
            <span className="text-xs md:text-sm">{t('futures.duration')}</span>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-8 flex-1">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : positionHistoryData.length > 0 ? (
            <div className="space-y-3 flex-1 flex flex-col">
              {/* Position History Items */}
              <div className="space-y-3 flex-1 overflow-auto" translate="no">
                {currentHistoryItems.map((position) => (
                  <div key={position.id} className="flex flex-wrap items-center justify-between bg-slate-900/30 px-3 md:px-6 py-3 md:py-4 rounded-xl hover:bg-slate-900/50 transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50 shadow-lg">
                    <div className="w-full md:w-auto mb-2 md:mb-0">
                      <span className="text-slate-300 text-xs md:text-sm font-mono">
                        {new Date(position.closeTime).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    
                    <div className="w-1/2 md:w-auto mb-2 md:mb-0">
                      <span className="text-white font-medium text-sm md:text-base">{position.symbol}</span>
                    </div>
                    
                    <div className="w-1/2 md:w-auto mb-2 md:mb-0 text-right md:text-left">
                      <span className={`text-xs md:text-sm font-bold px-2 py-1 rounded-lg ${
                        position.side === 'long' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                      }`}>
                        {position.side.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="w-1/2 md:w-auto mb-2 md:mb-0">
                      <div className="flex md:hidden">
                        <div className="w-1/2">
                          <div className="text-xs text-slate-400">{t('futures.entryPrice')}</div>
                          <span className="text-slate-300 font-mono text-xs md:text-sm">{position.entryPrice.toFixed(getPricePrecision(position.symbol))}</span>
                        </div>
                        <div className="w-1/2">
                          <div className="text-xs text-slate-400">Exit</div>
                          <span className="text-slate-300 font-mono text-xs md:text-sm">{position.exitPrice.toFixed(getPricePrecision(position.symbol))}</span>
                        </div>
                      </div>
                      <span className="hidden md:inline text-slate-300 font-mono text-xs md:text-base">{position.entryPrice.toFixed(getPricePrecision(position.symbol))}</span>
                    </div>
                    
                    <div className="hidden md:block text-slate-300 font-mono text-xs md:text-base">{position.exitPrice.toFixed(getPricePrecision(position.symbol))}</div>
                    
                    <div className="w-1/2 md:w-auto mb-2 md:mb-0">
                      <div className="text-xs text-slate-400 md:hidden">{t('futures.pnl')}</div>
                      <span className={`font-mono text-xs md:text-base ${position.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {position.pnl.toFixed(2)}
                      </span>
                    </div>
                    
                    <div className={`hidden md:flex items-center ${position.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {position.roi >= 0 ? (
                        <TrendingUp size={14} className="mr-1" />
                      ) : (
                        <TrendingDown size={14} className="mr-1" />
                      )}
                      <span className="text-xs md:text-sm">{position.roi.toFixed(2)}%</span>
                    </div>
                    
                    <div className="w-full md:w-auto mt-2 md:mt-0 text-right md:text-left">
                      <span className="text-slate-300 text-xs md:text-sm flex items-center justify-end md:justify-start">
                        <Clock size={12} className="mr-1 text-slate-400" />
                        {formatDuration(position.durationSeconds)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                      currentPage === 1
                        ? 'bg-slate-800/30 text-slate-500 cursor-not-allowed'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 hover:text-white border border-slate-600/30 hover:border-slate-500/50'
                    }`}
                  >
                    <ChevronLeft size={16} />
                    {t('common.back')}
                  </button>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-sm">Page {currentPage} of {totalPages}</span>
                    <span className="text-slate-500 text-xs">
                      ({indexOfFirstItem + 1}-{Math.min(indexOfLastItem, positionHistoryData.length)} of {positionHistoryData.length})
                    </span>
                  </div>
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                      currentPage === totalPages
                        ? 'bg-slate-800/30 text-slate-500 cursor-not-allowed'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 hover:text-white border border-slate-600/30 hover:border-slate-500/50'
                    }`}
                  >
                    {t('common.next')}
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 flex-1">
              <Package size={64} className="mb-6 opacity-30" />
              <p className="text-lg">{t('futures.noPositionHistory')}</p>
              <p className="text-sm text-slate-600 mt-2">{t('futures.historyWillAppear')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FuturesMyOrders;